import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Trophy, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface LocationState {
  user: any; // User data from signup
}

const CompleteSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useAuth();
  const state = location.state as LocationState;

  const [selectedSports, setSelectedSports] = useState<string[]>(['chess']); // Default chess selected
  const [loading, setLoading] = useState(false);

  const sports = [
    { id: 'football', name: 'Football', icon: '⚽' },
    { id: 'cricket', name: 'Cricket', icon: '🏏' },
    { id: 'tennis', name: 'Tennis', icon: '🎾' },
    { id: 'chess', name: 'Chess', icon: '♟️' },
    { id: 'basketball', name: 'Basketball', icon: '🏀' },
    { id: 'swimming', name: 'Swimming', icon: '🏊' },
    { id: 'badminton', name: 'Badminton', icon: '🏸' },
    { id: 'athletics', name: 'Athletics', icon: '🏃' }
  ];

  // Redirect if no user data
  useEffect(() => {
    if (!state?.user) {
      toast.error('No user data found. Please sign up again.');
      setTimeout(() => navigate('/signup'), 2000);
    }
  }, [state, navigate]);

  const handleSportToggle = (sportId: string) => {
    setSelectedSports(prev => 
      prev.includes(sportId)
        ? prev.filter(id => id !== sportId)
        : [...prev, sportId]
    );
  };

  const handleCompleteSignup = async () => {
    if (selectedSports.length === 0) {
      toast.error('Please select at least one sport');
      return;
    }

    setLoading(true);

    try {
      // Update user's sports preferences
      const response = await api.put('/auth/update-profile', {
        sports: selectedSports
      });

      if (response.data.success) {
        // Update user data in context (token is already stored from signup)
        setUserData(response.data.data.user);
        
        // Show different success messages based on role
        if (state?.user?.role === 'coach') {
          toast.success('Profile completed! Please wait for admin approval. 🎉');
        } else {
          toast.success('Welcome to Sports Community! 🎉');
        }
        
        // Navigate to home page (user dashboard)
        navigate('/home');
      }
    } catch (error: any) {
      console.error('Complete signup error:', error);
      const errorMessage = error.message || error.response?.data?.error?.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!state?.user) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
          {/* Success Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Account Created! 🎉
            </h1>
            <p className="text-sm sm:text-base text-gray-600 px-2">
              Welcome {state?.user?.firstName}! Let's complete your profile by selecting your sports interests
              {state?.user?.role === 'coach' && (
                <span className="block mt-1 text-xs sm:text-sm text-yellow-600">
                  As a coach, your account will be reviewed by our admin team after completing this step.
                </span>
              )}
            </p>
          </div>

          {/* Progress Steps - Hidden on mobile, shown on tablet+ */}
          <div className="hidden sm:flex items-center justify-center mb-8 overflow-x-auto">
            <div className="flex items-center space-x-2 lg:space-x-4">
              <div className="flex items-center flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="ml-2 text-xs lg:text-sm text-green-600 font-medium whitespace-nowrap">Account Created</span>
              </div>
              <div className="w-4 lg:w-8 h-1 bg-blue-200 flex-shrink-0"></div>
              <div className="flex items-center flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <span className="ml-2 text-xs lg:text-sm text-blue-600 font-medium whitespace-nowrap">Select Sports</span>
              </div>
              <div className="w-4 lg:w-8 h-1 bg-gray-200 flex-shrink-0"></div>
              <div className="flex items-center flex-shrink-0">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-gray-600" />
                </div>
                <span className="ml-2 text-xs lg:text-sm text-gray-500 font-medium whitespace-nowrap">Complete</span>
              </div>
            </div>
          </div>

          {/* Mobile Progress Indicator */}
          <div className="sm:hidden mb-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">Step 2 of 3</p>
          </div>

          {/* Sports Selection */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 text-center">
              What sports are you interested in?
            </h2>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-4 sm:mb-6 px-2">
              Select all that apply. You can always change this later in your profile.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {sports.map((sport) => (
                <button
                  key={sport.id}
                  onClick={() => handleSportToggle(sport.id)}
                  className={`
                    p-3 sm:p-4 rounded-xl border-2 transition-all duration-200 text-center touch-manipulation
                    ${selectedSports.includes(sport.id)
                      ? 'border-blue-500 bg-blue-50 text-blue-700 scale-95'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    }
                  `}
                >
                  <div className="text-xl sm:text-2xl mb-1 sm:mb-2">{sport.icon}</div>
                  <div className="text-xs sm:text-sm font-medium">{sport.name}</div>
                </button>
              ))}
            </div>

            {selectedSports.length > 0 && (
              <div className="mt-4 text-center">
                <p className="text-xs sm:text-sm text-gray-600">
                  Selected: {selectedSports.length} sport{selectedSports.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>

          {/* Complete Button */}
          <div className="text-center">
            <button
              onClick={handleCompleteSignup}
              disabled={selectedSports.length === 0 || loading}
              className={`
                w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-semibold text-base sm:text-lg transition-all duration-200
                ${selectedSports.length > 0 && !loading
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl active:scale-95'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }
              `}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                  <span className="text-sm sm:text-base">
                    {state?.user?.role === 'coach' ? 'Completing profile...' : 'Creating your account...'}
                  </span>
                </div>
              ) : (
                <span className="text-sm sm:text-base">
                  {state?.user?.role === 'coach' ? 'Complete Profile 🎯' : 'Complete Registration 🚀'}
                </span>
              )}
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-4 sm:mt-6 text-center px-2">
            <p className="text-xs text-gray-500">
              By completing registration, you agree to our{' '}
              <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteSignupPage;